import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReactGrid, Column, CellChange, NumberCell, TextCell, HeaderCell } from "@silevis/reactgrid";
import { Select } from "barte-design-system";

import "./card-network-grid.scss";

import { Skeleton } from "@shared/components";
import type { CardNetworkProps } from "./card-network.type";
import { ICompanyPlanTaxCreditMethod, ECompanyPlanTaxCreditMethod } from "@shared/types";
import styles from "./card-network.module.css";
import { BrandHeaderCellTemplate, BrandHeaderCellProps } from "./components/brand-header";

const INSTALLMENT_COL_WIDTH = 60;
const BRAND_COL_WIDTH = 126;

export const CardNetwork = ({
    data,
    isLoading,
    onChange
}: CardNetworkProps) => {
    const { t } = useTranslation();

    const allBrands = useMemo(() => Object.keys(ECompanyPlanTaxCreditMethod), []);
    const installments = useMemo(() => Array.from({ length: 24 }, (_, i) => i + 1), []);

    const activeBrands = useMemo(() => {
        return allBrands.filter(brand => brand === ECompanyPlanTaxCreditMethod.default || !!data?.[brand]);
    }, [allBrands, data]);

    const availableBrands = useMemo(() => {
        return allBrands.filter(brand => !activeBrands.includes(brand));
    }, [allBrands, activeBrands]);

    const handleAddBrand = (brand: string) => {
        if (!brand || !data) return;

        const defaultRates = data?.default?.sellerRates || Array(24).fill(0);

        const newData = {
            ...data,
            [brand]: { sellerRates: [...defaultRates] },
        };

        onChange(newData as ICompanyPlanTaxCreditMethod);
    };

    const handleRemoveBrand = (brand: string) => {
        const newData = { ...data } as any;
        delete newData[brand];
        onChange(newData);
    };

    const columns = useMemo((): Column[] => [
        { columnId: "installment", width: INSTALLMENT_COL_WIDTH },
        ...activeBrands.map(brand => ({ columnId: brand, width: BRAND_COL_WIDTH }))
    ], [activeBrands]);

    const totalGridWidth = useMemo(() =>
        INSTALLMENT_COL_WIDTH + (activeBrands.length * BRAND_COL_WIDTH),
        [activeBrands]);

    const rows = useMemo(() => {
        const headerRow: any = {
            rowId: "header",
            cells: [
                { type: "header", text: t("backoffice.pageModalCompanyPlanInstallmentLabel") } as HeaderCell,
                ...activeBrands.map(brand => {
                    const label = t(`backoffice.pageModalCompanyPlanBrand${brand.charAt(0).toUpperCase() + brand.slice(1)}`);
                    return {
                        type: "brand-header",
                        text: label,
                        onRemove: brand === "default" ? undefined : () => handleRemoveBrand(brand),
                        className: "brand-header"
                    } as BrandHeaderCellProps;
                })
            ]
        };

        const dataRows: any[] = installments.map((inst, index) => ({
            rowId: String(inst),
            cells: [
                { type: "text", text: ` ${inst}x`, nonEditable: true, style: { textAlign: "center", width: "100%", display: "block" } } as TextCell,
                ...activeBrands.map(brand => {
                    const brandData = data?.[brand as keyof ICompanyPlanTaxCreditMethod];
                    const rate = brandData?.sellerRates?.[index] ?? 0;
                    return {
                        type: "number",
                        value: Number(rate) || 0,
                        format: new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2 })
                    } as NumberCell;
                })
            ]
        }));

        return [headerRow, ...dataRows];
    }, [t, activeBrands, installments, data]);

    const handleChanges = (changes: CellChange<NumberCell>[]) => {
        const newData = { ...(data || {}) } as any;

        changes?.forEach((change) => {
            const brand = change.columnId as string;
            const installmentIndex = Number(change.rowId) - 1;

            if (activeBrands.includes(brand as any) && !isNaN(installmentIndex)) {
                const brandData = { ...(newData[brand] || { sellerRates: Array(24).fill(0) }) };
                const newSellerRates = [...(brandData.sellerRates || Array(24).fill(0))];

                newSellerRates[installmentIndex] = change.newCell.value;
                newData[brand] = { ...brandData, sellerRates: newSellerRates };
            }
        });

        onChange(newData);
    };

    if (isLoading) {
        return (
            <div className={styles.table}>
                <Skeleton width="100%" height="20rem" />
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            {availableBrands.length > 0 && (
                <div className={styles['add-brand']}>
                    <Select
                        placeholder={t("backoffice.pageRatePlanModalAddNetwork")}
                        options={availableBrands.map(brand => ({
                            label: t(`backoffice.pageModalCompanyPlanBrand${brand.charAt(0).toUpperCase() + brand.slice(1)}`),
                            value: brand
                        }))}
                        value=""
                        onChange={(e: any) => handleAddBrand(e?.value || e)}
                        hidePrefixIcon
                    />
                </div>
            )}

            <div className={styles['grid-container']} style={{ width: `${totalGridWidth + 2}px` }}>
                <ReactGrid
                    rows={rows as any}
                    columns={columns}
                    onCellsChanged={handleChanges}
                    stickyTopRows={1}
                    stickyLeftColumns={1}
                    enableRangeSelection
                    enableRowSelection
                    enableColumnSelection
                    enableFullWidthHeader
                    moveRightOnEnter
                    enableFillHandle
                    customCellTemplates={{
                        "brand-header": new BrandHeaderCellTemplate()
                    }}
                />
            </div>
        </div>
    );
};
